import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { VStack } from '@cbhq/cds-web/layout';
import { Spinner } from '@cbhq/cds-web/loaders/Spinner';

type LoaderQueue = Record<string, boolean>;

type InitialLoaderReturn = () => {
  setLoading: (loading: boolean, id: string) => void;
  initialLoading: boolean;
};

export const InitialLoaderContext = createContext<InitialLoaderReturn>(() => {
  throw new Error('InitialLoaderContext used without a provider');
});

type InitialLoaderProps = { children: React.ReactNode };

const INITIAL_LOADER_BUFFER = 500;

// exported for the test
export const INITIAL_LOADER_TIMEOUT = 1000 * 10;

export function InitialLoaderProvider({ children }: InitialLoaderProps) {
  const [loaderQueue, setLoaderQueue] = useState<LoaderQueue>({});
  const stageQueueRef = useRef<LoaderQueue>({});
  const bufferRef = useRef<NodeJS.Timeout>();

  const isLoading = Object.values(loaderQueue).some((loading) => loading) ?? false;

  const setLoading = useCallback((loading: boolean, id: string) => {
    stageQueueRef.current = { ...stageQueueRef.current, [id]: loading };
    setLoaderQueue((currentQueue) => {
      const isCurrentlyLoading = Object.values(currentQueue).some((l) => l);
      const willBeLoading = Object.values(stageQueueRef.current).some((x) => x);
      if (bufferRef.current) {
        clearTimeout(bufferRef.current);
        bufferRef.current = undefined;
      }
      if (isCurrentlyLoading && !willBeLoading) {
        bufferRef.current = setTimeout(() => {
          setLoaderQueue(stageQueueRef.current);
        }, INITIAL_LOADER_BUFFER);
        return currentQueue;
      }
      return stageQueueRef.current;
    });
  }, []);

  useEffect(() => {
    if (!isLoading) {
      return undefined;
    }

    const timeout = setTimeout(() => {
      setLoaderQueue({});
      stageQueueRef.current = {};

      if (bufferRef.current) {
        clearTimeout(bufferRef.current);
      }
    }, INITIAL_LOADER_TIMEOUT);

    return () => {
      clearTimeout(timeout);
    };
  }, [isLoading]);

  useEffect(
    () => () => {
      if (bufferRef.current) {
        clearTimeout(bufferRef.current);
        bufferRef.current = undefined;
      }
    },
    [],
  );

  const value = useCallback(
    () => ({ setLoading, initialLoading: isLoading }),
    [isLoading, setLoading],
  );

  return (
    <InitialLoaderContext.Provider value={value}>
      <>
        {isLoading && (
          <VStack alignItems="center" justifyContent="center" width="100%" height="100vh">
            <Spinner size={4} color="fgPrimary" />
          </VStack>
        )}
        <div
          style={{
            display: isLoading ? 'none' : undefined,
            width: '100%',
            height: '100%',
          }}
        >
          {children}
        </div>
      </>
    </InitialLoaderContext.Provider>
  );
}

export function useRegisterInitialLoader(value: boolean, id: string) {
  const { setLoading } = useContext(InitialLoaderContext)();
  useLayoutEffect(() => {
    setLoading(value, id);
    return () => setLoading(false, id);
  }, [id, value, setLoading]);
}
